import { useState, type FormEvent } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/app/auth";
import { clearPendingAuthLink } from "@/app/auth-links";
import { logger } from "@/lib/logger";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

/** Règles alignées sur la configuration Supabase Auth (12 caractères, lettres et chiffres). */
export function passwordProblem(p: string): string | null {
  if (p.length < 12) return "12 caractères minimum.";
  if (!/[a-z]/.test(p) || !/[A-Z]/.test(p)) return "Au moins une minuscule et une majuscule.";
  if (!/\d/.test(p)) return "Au moins un chiffre.";
  return null;
}

export default function SetPasswordPage() {
  const { session, loading } = useAuth();
  const nav = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (!loading && !session) return <Navigate to="/connexion" replace />;

  async function submit(e: FormEvent) {
    e.preventDefault();
    const problem = passwordProblem(password);
    if (problem) { setError(problem); return; }
    if (password !== confirm) { setError("Les deux mots de passe ne correspondent pas."); return; }
    setBusy(true); setError(null);
    const { error } = await supabase.auth.updateUser({ password });
    setBusy(false);
    if (error) {
      logger.error("auth.set_password", error);
      setError(/different|same/i.test(error.message) ? "Choisis un mot de passe différent de l'ancien." : "Impossible d'enregistrer le mot de passe. Réessaie.");
      return;
    }
    clearPendingAuthLink();
    logger.info("auth.password_set");
    nav("/", { replace: true });
  }

  return (
    <div className="flex min-h-screen items-center justify-center p-6">
      <div className="glass-strong w-full max-w-sm rounded-3xl p-8">
        <div className="mb-6 text-center">
          <div className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-2xl text-lg font-black text-white" style={{ background: "var(--gradient-primary)" }}>IC</div>
          <div className="font-heading text-2xl font-extrabold tracking-tight">IRONCLIC</div>
          <div className="mt-1 text-sm text-muted-foreground">Choisis ton mot de passe</div>
        </div>
        <form onSubmit={submit} className="space-y-4" noValidate>
          <div className="space-y-1.5">
            <Label htmlFor="password">Nouveau mot de passe</Label>
            <Input id="password" type="password" autoComplete="new-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
            <p className="text-xs text-muted-foreground">12 caractères minimum, avec majuscule, minuscule et chiffre.</p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="confirm">Confirmation</Label>
            <Input id="confirm" type="password" autoComplete="new-password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} />
          </div>
          {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
          <Button type="submit" className="w-full" disabled={busy}>Enregistrer et continuer</Button>
        </form>
      </div>
    </div>
  );
}
